import * as R from 'ramda';
import Zousan from 'zousan';

import type { PluginApp } from '@typings/plugin-runtime.ts';
import { buildStructuresLookup } from '@utils/buildStructureLookup.ts';
import { debugIsTrue } from '@utils/configUtils.ts';
import { toLang } from '@utils/i18n.ts';

import type { PluginConfig } from '../config-schema.ts';
import { computePoiOpenStatus } from './computeIsOpenNow.ts';

async function create(app: PluginApp, config?: PluginConfig) {
  const log = app.log.sublog('poiDataManager');
  // Active by default; a venue opts out with disableOpenStatus: true
  const isOpenStatusEnabled = config?.disableOpenStatus !== true;

  const init = () => {
    app.bus.send('venueData/loadPoiData');
  };

  let poisLoaded = new Zousan();

  const fixPositionInfo = (poi, structuresLookup) => {
    const { position } = poi;
    const structure = structuresLookup.floorIdToStructure(position.floorId);
    if (!structure) {
      log.error(`No structure found for floorId: ${position.floorId} for POI ${poi.poiId}`);
      return { ...poi };
    }
    const floor = structuresLookup.floorIdToFloor(position.floorId);
    const detailedPosition = {
      ...position,
      structureName: poi.structureNameOverride || structure.name,
      buildingId: structure.id,
      floorName: poi.levelNameOverride || floor.name,
      floorOrdinal: floor.ordinal,
    };
    return { ...poi, position: detailedPosition };
  };

  // todo R.map may be enough to update dictionary values
  const formatPois = (pois, structuresLookup) => {
    return R.pipe(
      R.values,
      R.map(poi => {
        poi.distance = null;
        poi.staticName = poi.name;
        poi.isNavigable = poi.isNavigable === undefined || poi.isNavigable === true; // isNavigable is true as default, and is optional in the POI data
        // poi.isNavigable = /^[a-mA-M]+/.test(poi.name) // uncomment for easy testing of isNavigable
        if (poi.capacity) {
          addToRoomInfo(poi, {
            name: `Seats ${poi.capacity.join('-')}`,
            svgId: 'number-of-seats',
          });
        }
        if (poi.category.startsWith('meeting')) {
          addToRoomInfo(poi, {
            name: app.gt()('poiView:Conference Room'),
            svgId: 'conference-room',
          });
        }

        const roomId = getRoomId(poi);
        if (roomId) {
          poi.roomId = roomId;
        }

        return [poi.poiId, fixPositionInfo(poi, structuresLookup)];
      }),
      R.fromPairs,
    )(pois);
  };

  const addToRoomInfo = (poi, value) => {
    if (!poi.roomInfo) {
      poi.roomInfo = [];
    }
    poi.roomInfo.push(value);
  };

  const getRoomId = R.pipe(
    R.propOr([], 'externalIds'),
    R.find(R.propEq('roomId', 'type')),
    R.prop('id'),
    R.unless(R.isNil, R.tail),
  );

  async function checkNavgraph(pois) {
    const start = Date.now();
    const badPois = [];
    const navGraph = await app.bus.get('wayfinder/_getNavGraph');
    Object.values(pois).forEach(poi => {
      try {
        const pos = poi.position;
        const n = navGraph.findClosestNode(pos.floorId, pos.latitude, pos.longitude);
        if (!n) {
          badPois.push({ id: poi.poiId, e: 'No closest Navgraph Node' });
        }
      } catch (e) {
        log.error(e);
        badPois.push({ id: poi.poiId, e: e.message });
      }
    });

    if (badPois.length) {
      log.warn('badPois:', badPois);
    }

    log(`Total time for navgraph POI check: ${Date.now() - start}ms`);
  }

  function filterBadPois(pois) {
    const badPois = [];
    Object.values(pois).forEach(poi => {
      try {
        const pos = poi.position;
        if (!pos) {
          badPois.push({ poi, e: 'No position information' });
        } else {
          ['buildingId', 'structureName', 'floorId', 'floorName', 'floorOrdinal', 'latitude', 'longitude'].forEach(
            k => {
              if (pos[k] === null || pos[k] === undefined) {
                badPois.push({
                  id: poi.poiId,
                  e: `invalid position property: ${k}: ${pos[k]}`,
                });
              }
            },
          );
        }
      } catch (e) {
        log.error(e);
        badPois.push({ id: poi.poiId, e: e.message });
      }
    });

    if (badPois.length) {
      log.warn('badPois:', badPois);
      badPois.forEach(ob => {
        delete pois[ob.id];
      });
    }

    return pois;
  }

  async function enhanceImages(pois) {
    for (const poi of Object.values(pois)) {
      await addImages(poi);
    }

    return pois;
  }

  app.bus.on('venueData/poiDataLoaded', async ({ pois, structures }) => {
    const structuresLookup = buildStructuresLookup(structures);
    pois = formatPois(pois, structuresLookup);
    if (debugIsTrue(app, 'pseudoTransPois')) {
      for (const id in pois) {
        pois[id] = pseudoTransPoi(pois[id], app.i18n().language);
      }
    }

    pois = filterBadPois(pois);

    await enhanceImages(pois);

    poisLoaded.resolve(pois);

    if (app.config.debug && app.env.isBrowser) {
      window._pois = pois;
    }

    if (app.config.debug) {
      checkNavgraph(pois);
    } // this doesn't change the pois, but warns in the console...
  });

  app.bus.on('poi/getById', async ({ id }) => {
    return poisLoaded.then(pois => pois[id]);
  });

  app.bus.on('poi/getByFloorId', async ({ floorId }) =>
    poisLoaded.then(R.pickBy(R.pathEq(floorId, ['position', 'floorId']))),
  );

  app.bus.on('poi/getByCategoryId', async ({ categoryId }) => {
    // returns true for exact category matches or parent category matches
    const categoryMatch = poi => poi.category === categoryId || poi.category.startsWith(categoryId + '.');
    return poisLoaded.then(R.pickBy(categoryMatch));
  });

  app.bus.on('poi/getAll', async () => poisLoaded);

  /**
   * Returns the current open/closed status for a POI.
   *
   * This is computed on demand rather than cached, because the answer depends on the
   * current time: a POI that is open at 16:59 is closed at 17:01. Callers should request
   * the status at the moment they need to render it, and must not write the result back
   * onto the POI object.
   *
   * Resolves to `{ isOpen, status, timeLabel }`, or `undefined` when the status cannot be
   * determined or the feature is disabled. Consumers should branch on the locale-independent
   * `status` value; `timeLabel` is translated display text only.
   */
  app.bus.on('poi/getOpenStatus', async ({ id, now }) => {
    if (!isOpenStatusEnabled) {
      return undefined;
    }
    const pois = await poisLoaded;
    const poi = pois[id];
    if (!poi) {
      return undefined;
    }
    const venueTimezone = await app.bus.get('venueData/getVenueTimezone');
    return computePoiOpenStatus(poi, venueTimezone, app.gt(), now ?? new Date());
  });

  const checkpointPath = ['queue', 'primaryQueueId'];
  const addOtherSecurityLanesIfNeeded = async poi => {
    const primaryCheckpointId = R.path(checkpointPath, poi);

    if (!primaryCheckpointId) {
      return poi;
    }

    const queueTypes = await app.bus.get('venueData/getQueueTypes');
    const securityPoisMap = await app.bus.get('poi/getByCategoryId', {
      categoryId: 'security',
    });
    const securityPoisList = Object.values(securityPoisMap);

    poi.queue.otherQueues = prepareOtherSecurityLanes(queueTypes, poi, securityPoisList);
    return poi;
  };

  const prepareOtherSecurityLanes = (queueTypes, currentPoi, securityPois) => {
    const queueTypePath = ['queue', 'queueType'];
    const queueType = R.path(queueTypePath, currentPoi);
    if (!queueType) {
      return null;
    }

    const queueSubtypes = queueTypes[queueType];
    const primaryCheckpointId = R.path(checkpointPath, currentPoi);
    return securityPois
      .filter(R.pathEq(primaryCheckpointId, checkpointPath)) // filter only connected security checkpoints
      .filter(poi => poi.poiId !== currentPoi.poiId) // skip current poi
      .map(poi => {
        const laneId = R.path(['queue', 'queueSubtype'], poi);
        const lane = getLaneData(laneId)(queueSubtypes);
        return { poiId: poi.poiId, ...lane };
      });
  };

  // if the value passed is non-null, simply return it. Else throw the error with the message specified.
  // This is useful to insert into pipelines.
  const ensureDefined = errorMsg => value => {
    if (value != null) {
      return value;
    }
    throw Error(errorMsg);
  };

  const getLaneData = laneId =>
    R.pipe(
      R.find(R.propEq(laneId, 'id')),
      ensureDefined(`No queue found with ID: ${laneId}`),
      R.pick(['displayText', 'imageId']),
    );

  /**
   * Updates security checkpoint POI with a list of related security checkpoints.
   */
  app.bus.on('poi/addOtherSecurityLanes', ({ poi }) => addOtherSecurityLanesIfNeeded(poi));

  const isFullUrl = url => !!url?.startsWith('https:');

  async function addImages(poi) {
    if (!poi) {
      return;
    }
    const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
    const size = `${Math.round(351 * dpr)}x${Math.round(197 * dpr)}`;

    if (!R.length(poi.images)) {
      poi.images = [];
    } else if (!isFullUrl(poi.images[0])) {
      // then images are not yet transformed
      poi.images = await Zousan.all(
        poi.images.map(imageName => app.bus.get('venueData/getPoiImageUrl', { imageName, size })),
      );
    }

    if (!R.length(poi.fullImages)) {
      poi.fullImages = [];
    } else if (!isFullUrl(poi.fullImages[0]?.url)) {
      // then images are not yet transformed
      poi.fullImages = await Zousan.all(
        poi.fullImages.map(async ({ url, ...rest }) => {
          const fullUrl = await app.bus.get('venueData/getPoiImageUrl', {
            imageName: url,
            size,
          });
          return { url: fullUrl, ...rest };
        }),
      );
    }

    return poi;
  }

  const getUniqueCategories = R.memoizeWith(R.identity, R.pipe(R.pluck('category'), R.values, R.uniq));
  app.bus.on('poi/getAllCategories', async () => poisLoaded.then(getUniqueCategories));

  app.bus.on('venueData/loadNewVenue', () => {
    poisLoaded = new Zousan();
    init();
  });

  app.bus.on('poi/setDynamicData', async ({ plugin, idValuesMap }) => {
    const pois = await poisLoaded;

    for (const poiId in idValuesMap) {
      const dynamicData = pois[poiId].dynamicData || {};
      dynamicData[plugin] = { ...idValuesMap[poiId] };
      const newPoi = R.mergeRight(pois[poiId], { dynamicData });
      pois[poiId] = newPoi;
    }
  });

  /**
   * Applies or reverts dynamic name overrides on POIs.
   * A null name value reverts to staticName (original published name).
   * Emits poi/updated event with updated POIs for search re-indexing.
   */
  app.bus.on('poi/setCoreAttributes', idAttributesMap => {
    poisLoaded.then(pois => {
      const updatedPois = [];
      for (const poiId in idAttributesMap) {
        const changed = applyPoiNameOverride(pois, poiId, idAttributesMap[poiId]?.name);
        if (changed) {
          app.bus.send('map/mutateFeature', { poiId, name: pois[poiId].name });
          updatedPois.push(pois[poiId]);
        }
      }
      if (updatedPois.length > 0) {
        app.bus.send('poi/updated', { pois: updatedPois });
      }
    });
  });

  const runTest = async testRoutine => {
    await testRoutine();
    return poisLoaded;
  };

  return {
    init,
    runTest,
    internal: {
      addImages,
      fixPositionInfo,
      pseudoTransPoi,
      applyPoiNameOverride,
    },
  };
}

export { create, applyPoiNameOverride };

/**
 * Applies or reverts a POI name override.
 *
 * - A `null` name reverts the POI to its staticName (original published name).
 * - Any other name value applies the override, leaving staticName unchanged.
 *
 * @param {Object} pois - Mutable map of POI id → POI object.
 * @param {string} poiId - ID of the POI to update.
 * @param {string|null} name - Name override to apply, or null to revert to staticName.
 * @returns {boolean} - true if the name was changed, false otherwise.
 */
function applyPoiNameOverride(pois, poiId, name) {
  if (!pois[poiId]) {
    return false;
  }
  if (!name) {
    // Revert to staticName (original published name)
    pois[poiId].name = pois[poiId].staticName;
  } else {
    // Apply override (staticName remains unchanged)
    pois[poiId].name = name;
  }
  return true;
}

function pseudoTransPoi(poi, lang) {
  ['description', 'nearbyLandmark', 'name', 'phone', 'operationHours'].forEach(p => {
    if (poi[p]) {
      poi[p] = toLang(poi[p], lang);
    }
  });
  if (poi.keywords) {
    poi.keywords = poi.keywords.map(keyword => {
      keyword.name = toLang(keyword.name, lang);
      return keyword;
    });
  }
  if (poi.position.floorName) {
    poi.position.floorName = toLang(poi.position.floorName, lang);
  }
  if (poi.position.structureName) {
    poi.position.structureName = toLang(poi.position.structureName, lang);
  }
  return poi;
}
